import flow from "lodash/fp/flow";
import getOr from "lodash/fp/getOr";
import omit from "lodash/fp/omit";
import split from "lodash/fp/split";

import selectUserChoiceValues from "./selectUserChoiceValues";
import selectSelectedProduct from "./selectSelectedProduct";
import selectProductSku from "./selectProductSku";
import selectMaterialID from "./selectMaterialID";
import { getGoalieGearAddToOrderValues } from "utils";

const ignoreValuesFor = [
  "SkateCustomiseBlade",
  "customizeBladePlayerName",
  "customizeBladePlayerNumber",
  "customizeBladeProfile",
  "customizeBladeSharpening",
  "playerName",
  "playerNumber",
  "size_left",
  "size_right",
  "noPersonalization",
  "customizeBladeNoPersonalization",
  "addExtension",
  "engineeredFor",
  "colorZone1",
  "colorZone2",
  "ColorZone2__HIDDEN",
  "colorZone2__HIDDEN",
  "Remove PentaGrip",
  "Add PentaGrip",
  "Shaft Color code (Vapor)",
  "Paddle Color code (Vapor)",
];

const playerStickColourCodeMap = {
  RED: "D001",
  BLUE: "D002",
  GREEN: "D003",
  NAVY: "D012",
  ORANGE: "D006",
  MAROON: "D007",
  "SPORTS GOLD": "D010",
  "METALLIC GOLD": "D004",
  WHITE: "D027",
  SILVER: "D013",
  YELLOW: "D022",
  CANADA: "D023",
  USA: "D024",
  BLACK: "D050",
  "ARIZONA RED": "D051",
  PURPLE: "D053",
  TEAL: "D052",
  RETAIL: "C001",
  STOCK: "C001",
  PINK: "D005",

  "WHITE/RED": "D001",
  "WHITE/BLUE": "D002",
  "WHITE/GREEN": "D003",
  "WHITE/NAVY": "D012",
  "WHITE/ORANGE": "D006",
  "WHITE/MAROON": "D007",
  "WHITE/SPORTS GOLD": "D010",
  "WHITE/METALLIC GOLD": "D004",
  "WHITE/WHITE": "D027",
  "WHITE/SILVER": "D013",
  "WHITE/BLACK": "D011",
  "WHITE/ARIZONA RED": "D051",
  "WHITE/TEAL": "D052",
  "WHITE/PURPLE": "D053",

  "BLACK/RED": "D061",
  "BLACK/BLUE": "D062",
  "BLACK/GREEN": "D065",
  "BLACK/NAVY": "D063",
  "BLACK/ORANGE": "D064",
  "BLACK/MAROON": "D068",
  "BLACK/SPORTS GOLD": "D067",
  "BLACK/METALLIC GOLD": "D066",
  "BLACK/WHITE": "D070",
  "BLACK/SILVER": "D072",
  "BLACK/BLACK": "D050",
  "BLACK/ARIZONA RED": "D073",
  "BLACK/TEAL": "D071",
  "BLACK/PURPLE": "D074",
};

function getSizeFromFlex(flex) {
  return {
    30: "Junior",
    "Flex 30": "Junior",
    40: "Junior",
    "Flex 40": "Junior",
    50: "Junior",
    "Flex 50": "Junior",
    55: "Intermediate",
    "Flex 55": "Intermediate",
    65: "Intermediate",
    "Flex 65": "Intermediate",
    "65 SR": "Senior",
    "Flex 65 SR": "Senior",
    "65S": "Senior",
    70: "Senior",
    "Flex 70": "Senior",
    77: "Senior",
    "Flex 77": "Senior",
    82: "Senior",
    "Flex 82": "Senior",
    87: "Senior",
    "Flex 87": "Senior",
    95: "Senior",
    "Flex 95": "Senior",
    102: "Senior",
    "Flex 102": "Senior",
    112: "Senior",
    "Flex 112": "Senior",
  }[flex];
}

function getProductSpecificAddToOrderValues(state) {
  const product = selectSelectedProduct(state);
  const productSku = selectProductSku(state);
  const materialID = selectMaterialID(state);
  const userChoiceValues = selectUserChoiceValues(state);
  const [playerNameFirst = "", playerNameLast = ""] = flow(
    getOr([], ["app", "volumentalData", "user_profile", "name"]),
    split(" ")
  )(state);
  const { external_id, device_id } = getOr({}, [
    "app",
    "volumentalData",
    "scan",
  ])(state);

  const fromVolumentalData = {
    "Vandra Scanner Number": device_id,
    "Unique Foot Scan ID": external_id,
    "Player Name, First": playerNameFirst,
    "Player Name, Last": playerNameLast,
  };

  const colourZone2FallbackToZone1 = userChoiceValues["colorZone2"]
    ? "colorZone2"
    : "colorZone1";

  const goalieStickShaftColorCode = [
    userChoiceValues["Shoulder Color code"],
    userChoiceValues["Shaft Color code"],
  ]
    .filter(Boolean)
    .join("/");

  const getPlayerStickEndPlugOrderValues = ({ extensionLength }) => {
    return {
      "End Plug (aka extension)": userChoiceValues.addExtension
        ? `Add ${extensionLength}" Extension`
        : "Stock",
      "End Plug Code": userChoiceValues.addExtension
        ? `${extensionLength}`
        : "No",
    };
  };

  const genericValues = {
    productSku,
    Family: product?.family,
    ProductFamily: product?.productFamily,
    product: state?.app?.formData?.product,
    ...userChoiceValues,
    webAppValues: JSON.stringify(userChoiceValues),
  };

  const playerStickValues = {
    "Name Bar ('Nick Name')": userChoiceValues.playerName,
    Size: getSizeFromFlex(userChoiceValues.Flex),
    "Hand (aka curve or handedness)": userChoiceValues.Hand,
  };

  // -- Custom Goalie Sticks --------------------------------------------
  switch (product?.productFamily) {
    case "HyperliteGoalieStick2023":
      return {
        ...genericValues,
        customisationType: "Custom Goalie Stick",
        "Personalization (player name)": userChoiceValues.playerName,
        "Shaft Color text": userChoiceValues["Shaft Color code"],
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Bauer Logo Color text": userChoiceValues["Bauer Logo Color code"],
        "HYPERLITE Logo Color text":
          userChoiceValues["HYPERLITE Logo Color code"],
        "Stick Extension": userChoiceValues["Stick Extension"]
          ? 'Add 2" extension'
          : "Stock",
        PentaGrip: userChoiceValues["PentaGrip"] ? "Remove Pentagrip" : "Stock",
      };

    case "VaporGoalieStick2025":
      return {
        ...genericValues,
        customisationType: "Custom Goalie Stick",
        "Personalization (player name)": userChoiceValues.playerName,
        "Shoulder Decal Color code": userChoiceValues["Base Color"],
        "Shaft Color text": userChoiceValues["Shaft Color code"],
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Bauer Logo Color text": userChoiceValues["Bauer Logo Color code"],
        "Family Color": userChoiceValues["Flylite Logo Color code"],
        PentaGrip: userChoiceValues["PentaGrip"] ? "NO" : "PENTA",
      };

    case "MachGoalieStick2022":
    case "ShadowGoalieStick2024":
    case "FuseGoalieStick2026":
      return {
        ...genericValues,
        customisationType: "Custom Goalie Stick",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Shaft Color code": goalieStickShaftColorCode,
        "Shaft Color text": goalieStickShaftColorCode,
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Outline Color text": userChoiceValues["Outline Color code"],
        "Family Color": userChoiceValues["Spine Color code"],
        PentaGrip: userChoiceValues["PentaGrip"] ? "PENTA" : "NO",
      };

    case "ProCustomGoalieStick2025":
    case "ProCustomGoalieStick2026":
      if (userChoiceValues["Graphic"] === "Vapor") {
        return {
          ...genericValues,
          customisationType: "Pro Custom Goalie Stick",
          Graphic: "Vapor Flylite",
          "Personalization (player name)": userChoiceValues.playerName,
          "Shoulder Decal Color code": userChoiceValues["Base Color"],
          "Shaft Color code": userChoiceValues["Shaft Color code (Vapor)"],
          "Shaft Color text": userChoiceValues["Shaft Color code (Vapor)"],
          "Paddle Color code": userChoiceValues["Paddle Color code (Vapor)"],
          "Paddle Color text": userChoiceValues["Paddle Color code (Vapor)"],
          "Bauer Logo Color text": userChoiceValues["Bauer Logo Color code"],
          "Family Color": userChoiceValues["Flylite Logo Color code"],
          "Stick Extension": userChoiceValues["Stick Extension"]
            ? 'Add 2" extension'
            : "Stock",
          PentaGrip: userChoiceValues["Shoulder"]?.includes("Ergo Hybrid")
            ? "NO"
            : userChoiceValues["Remove PentaGrip"]
            ? "NO"
            : "PENTA",
          ...(product?.productFamily === "ProCustomGoalieStick2025"
            ? {
                Pattern:
                  userChoiceValues.Pattern === "P31"
                    ? "P31H"
                    : userChoiceValues.Pattern,
                "Handle Length": userChoiceValues["Handle Length"].replace(
                  / *\([^)]*\) */g,
                  ""
                ),
              }
            : {}),
          ...(product?.productFamily === "ProCustomGoalieStick2026"
            ? {
                Pattern: userChoiceValues.Pattern?.replace(
                  / *\([^)]*\) */g,
                  ""
                ),
                Shoulder: userChoiceValues.Shoulder?.replace(
                  / *\([^)]*\) */g,
                  ""
                ),
                "Handle Length": userChoiceValues["Handle Length"]?.replace(
                  / *\([^)]*\) */g,
                  ""
                ),
              }
            : {}),
        };
      } else if (userChoiceValues["Graphic"] === "Supreme") {
        return {
          ...genericValues,
          Graphic: "Supreme Fuse",
          customisationType: "Pro Custom Goalie Stick",
          ...(product?.productFamily === "ProCustomGoalieStick2026"
            ? {
                Family: "CustomSupremeGoalieStick",
                Pattern: userChoiceValues.Pattern?.replace(
                  / *\([^)]*\) */g,
                  ""
                ),
                Shoulder: userChoiceValues.Shoulder?.replace(
                  / *\([^)]*\) */g,
                  ""
                ),
                "Handle Length": userChoiceValues["Handle Length"]?.replace(
                  / *\([^)]*\) */g,
                  ""
                ),
              }
            : {}),
          ...(product?.productFamily === "ProCustomGoalieStick2025"
            ? {
                "Handle Length": userChoiceValues["Handle Length"].replace(
                  / *\([^)]*\) */g,
                  ""
                ),
              }
            : {}),
          "Name Bar ('Nick Name')": userChoiceValues.playerName,
          "Shaft Color code": goalieStickShaftColorCode,
          "Shaft Color text": goalieStickShaftColorCode,
          "Paddle Color text": userChoiceValues["Paddle Color code"],
          "Shoulder Decal Color text":
            userChoiceValues["Shoulder Decal Color code"],
          "Outline Color text": userChoiceValues["Outline Color code"],
          "Family Color": userChoiceValues["Spine Color code"],
          PentaGrip: userChoiceValues["Add PentaGrip"] ? "PENTA" : "NO",
        };
      } else {
        return;
      }

    // -- QT Goalie Sticks --------------------------------------------
    case "QuickTurnVaporFlyliteGoalieStick":
      return {
        ...genericValues,
        customisationType: "QuickTurn Goalie Stick",
        "Personalization (player name)": userChoiceValues.playerName,
        "Shoulder Color text": userChoiceValues["Base Color"],
        "Shaft Color text": userChoiceValues["Shaft Color code"],
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Bauer Logo Color text": userChoiceValues["Bauer Logo Color code"],
        "Stick Extension": userChoiceValues["Stick Extension"]
          ? 'Add 2" extension'
          : "Stock",
        PentaGrip: userChoiceValues["PentaGrip"] ? "NO" : "PENTA",
        // Below does not apply for 2026 stick as P31 is not an option
        Pattern:
          userChoiceValues.Pattern === "P31"
            ? "P31H"
            : userChoiceValues.Pattern,
      };

    case "QuickTurnSupremeShadowGoalieStick":
    case "QuickTurnSupremeFuseGoalieStick":
      return {
        ...genericValues,
        customisationType: "QuickTurn Goalie Stick",
        "Name Bar ('Nick Name')": userChoiceValues.playerName,
        "Shaft Color code": goalieStickShaftColorCode,
        "Shaft Color text": goalieStickShaftColorCode,
        "Paddle Color text": userChoiceValues["Paddle Color code"],
        "Shoulder Decal Color text":
          userChoiceValues["Shoulder Decal Color code"],
        "Outline Color text": userChoiceValues["Outline Color code"],
        "Family Color": userChoiceValues["Spine Color code"],
        PentaGrip: userChoiceValues["PentaGrip"] ? "PENTA" : "NO",
      };

    // -- Custom Player Sticks --------------------------------------------
    case "ProCustomPlayerStick2023":
      return {
        ...genericValues,
        customisationType: "Pro Custom Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 2,
        }),

        Graphic:
          userChoiceValues.Graphic === "Vapor"
            ? "Vapor Flylite"
            : userChoiceValues.Graphic === "Supreme"
            ? "Supreme Fuse"
            : userChoiceValues.Graphic,

        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,

        "Color Zone 1 (or country) code":
          userChoiceValues.colorZone1 &&
          playerStickColourCodeMap[userChoiceValues.colorZone1.toUpperCase()],
        "Color Zone 1 (or country) text": userChoiceValues.colorZone1,

        "Color Zone 2 (or country) code":
          userChoiceValues.colorZone1 &&
          playerStickColourCodeMap[
            userChoiceValues[colourZone2FallbackToZone1].toUpperCase()
          ],
        "Color Zone 2 (or country) text":
          userChoiceValues.colorZone1 &&
          userChoiceValues[colourZone2FallbackToZone1],
      };

    case "HyperlitePlayerStick2023":
    case "TWITCHPlayerStick2024":
    case "PROTO2PlayerStick2025":
      return {
        ...genericValues,
        customisationType: "Custom Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 2,
        }),

        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
      };

    case "PulsePlayerStick2025":
    case "Pulse2PlayerStick2026":
    case "ADVOnePlayerStick2026":
      const colorCode =
        (userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()]) ||
        "";

      return {
        ...genericValues,
        customisationType: "Custom Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 2,
        }),

        "Color (or country) code":
          // ADV One option value is "RAISED INK", Pulse is "Raised Ink"
          userChoiceValues.Grip?.toUpperCase() === "RAISED INK"
            ? colorCode.substring(0, 1) + "7" + colorCode.substring(2)
            : colorCode,
        "Color (or country) text": userChoiceValues.Color,
      };

    case "SupremePlayerStick2020":
    case "CustomPROTOR":
      return {
        ...genericValues,
        customisationType: "Custom Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 4,
        }),

        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
      };

    case "FlylitePlayerStick2025":
    case "TracerPlayerStick2024":
    case "SupremeFusePlayerStick2026":
      return {
        ...genericValues,
        customisationType: "Custom Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 2,
        }),

        "Color Zone 1 (or country) code":
          userChoiceValues["colorZone1"] &&
          playerStickColourCodeMap[
            userChoiceValues["colorZone1"].toUpperCase()
          ],
        "Color Zone 1 (or country) text": userChoiceValues["colorZone1"],

        "Color Zone 2 (or country) code":
          playerStickColourCodeMap[
            userChoiceValues[colourZone2FallbackToZone1].toUpperCase()
          ],
        "Color Zone 2 (or country) text":
          userChoiceValues[colourZone2FallbackToZone1],
      };

    // -- QT Player Sticks --------------------------------------------
    case "QuickTurnVaporStick":
    case "QuickTurnVaporFlylitePlayerStick":
    case "QuickTurnTwitchStick":
    case "QuickTurnPulsePlayerStick":
    case "QuickTurnPulse2PlayerStick":
    case "QuickTurnADVOnePlayerStick":
    case "QuickTurnSupremeStick":
    case "QuickTurnProtoR":
    case "QuickTurnProto2PlayerStick":
      return {
        ...genericValues,
        customisationType: "QuickTurn Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 4,
        }),

        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,
      };

    case "QuickTurnNexusStick":
    case "QuickTurnSupremeFusePlayerStick":
      return {
        ...genericValues,
        customisationType: "QuickTurn Player Stick",
        ...playerStickValues,
        ...getPlayerStickEndPlugOrderValues({
          extensionLength: 4,
        }),

        "Color (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color (or country) text": userChoiceValues.Color,

        "Color Zone 2 (or country) code":
          userChoiceValues.Color &&
          playerStickColourCodeMap[userChoiceValues.Color.toUpperCase()],
        "Color Zone 2 (or country) text": userChoiceValues.Color,
      };

    // -- Skates --------------------------------------------
    case "Hyperlite2Skate2023":
      return {
        productSku,
        product: state?.app?.formData?.product,
        SKU: productSku,
        ProductFamily: product?.productFamily,
        ...fromVolumentalData,
        ...userChoiceValues,
        "Number for Monogramming": userChoiceValues.playerNumber,
        "Name for Monogramming": userChoiceValues.playerName,
        "Blade Name": userChoiceValues.customizeBladePlayerName,
        "Blade Number": userChoiceValues.customizeBladePlayerNumber,
        "Left Length": userChoiceValues.size_left,
        "Right Length": userChoiceValues.size_right,
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "CustomSteel2023":
      return {
        productSku,
        product: state?.app?.formData?.product,
        SKU: productSku,
        ProductFamily: product?.productFamily,
        ...userChoiceValues,
        "Blade Name": userChoiceValues.customizeBladePlayerName,
        "Blade Number": userChoiceValues.customizeBladePlayerNumber,
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "SupremeMachSkate2022":
      return {
        productSku,
        product: state?.app?.formData?.product,
        SKU: productSku,
        ProductFamily: product?.productFamily,
        ...fromVolumentalData,
        ...userChoiceValues,
        "Number for Monogramming": userChoiceValues.playerNumber,
        "Name for Monogramming": userChoiceValues.playerName,
        "Blade Name": userChoiceValues.customizeBladePlayerName,
        "Blade Number": userChoiceValues.customizeBladePlayerNumber,
        "Left Length": userChoiceValues.size_left,
        "Right Length": userChoiceValues.size_right,
        Eyelets: "Standard",
        webAppValues: JSON.stringify(userChoiceValues),
      };

    case "ProCustomGoalierGear2023":
    case "VaporHyperliteGoalierGear2023":
    case "VaporFlyliteGoalieGear2025":
    case "VaporFlyliteGoalieGear2026":
    case "SVProGoalieGear2026":
    case "SupremeMachGoalierGear2023":
    case "SupremeShadowGoalieGear2024":
    case "SupremeFuseGoalieGear2026":
    case "DigiPrintGoalieGear2024":
      // console.log(`${product?.productFamily} RECIPE VALUES`, {
      //   ...getGoalieGearAddToOrderValues({
      //     userChoiceValues,
      //     product,
      //     materialID,
      //     state,
      //   }),
      //   webAppValues: JSON.stringify(userChoiceValues),
      // });
      return {
        ...getGoalieGearAddToOrderValues({
          userChoiceValues,
          product,
          materialID,
          state,
        }),
        webAppValues: JSON.stringify(userChoiceValues),
      };

    default:
      return {
        ...genericValues,
        webAppValues: JSON.stringify(userChoiceValues),
      };
  }
}

const selectAddToOrderValues = flow(
  getProductSpecificAddToOrderValues,
  omit(ignoreValuesFor)
);

export default selectAddToOrderValues;
